import React, { useEffect } from 'react';
import { AlertCircle, Database } from 'lucide-react';
import Header from '../components/landing/Header';
import Hero from '../components/landing/Hero';
import Stats from '../components/landing/Stats';
import Problems from '../components/landing/Problems';
import Features from '../components/landing/Features';
import HowItWorks from '../components/landing/HowItWorks';
import DeviceTypes from '../components/landing/DeviceTypes';
import Pricing from '../components/landing/Pricing';
import Testimonials from '../components/landing/Testimonials';
import FAQ from '../components/landing/FAQ';
import CTA from '../components/landing/CTA';
import Footer from '../components/landing/Footer';
import { isSupabaseConfigured } from '../lib/supabase';

function Landing() {
  // Set SEO meta tags
  useEffect(() => {
    // Update document title
    document.title = 'pMaster - Repair Shop CRM for Uzbekistan | Streamline Your Operations';

    // Update or create meta description
    const updateMetaTag = (name: string, content: string) => {
      let tag = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('name', name);
        document.head.appendChild(tag);
      }
      tag.content = content;
    };

    updateMetaTag(
      'description',
      'pMaster is a repair shop CRM designed for Uzbekistan. Streamline customer intake, diagnostics, payments, and more with AI-powered automation.'
    );

    updateMetaTag('keywords', 'repair shop CRM, Uzbekistan, pMaster, service management, shop software');

    updateMetaTag('og:title', 'pMaster - Repair Shop CRM for Uzbekistan');
    updateMetaTag(
      'og:description',
      'Manage your repair shop operations efficiently with pMaster - customer intake, diagnostics, payments, and real-time tracking.'
    );
    updateMetaTag('og:type', 'website');

    // Inject JSON-LD schema
    const schemaScript = document.getElementById('schema-org');
    if (schemaScript) {
      schemaScript.remove();
    }

    const script = document.createElement('script');
    script.id = 'schema-org';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Organization',
          name: 'pMaster',
          url: 'https://pmaster.uz',
          logo: 'https://pmaster.uz/logo.svg',
          sameAs: [
            'https://t.me/pmaster_bot',
            'https://instagram.com/pmaster_uz',
          ],
          contactPoint: {
            '@type': 'ContactPoint',
            contactType: 'Sales',
            telephone: '+998-95-221-21-44',
            email: 'info@thedevs.uz',
          },
        },
        {
          '@type': 'SoftwareApplication',
          name: 'pMaster',
          description:
            'Repair shop CRM for Uzbekistan market. Streamline customer intake, diagnostics, payments, and operations.',
          applicationCategory: 'BusinessApplication',
          offers: {
            '@type': 'Offer',
            price: '0',
            priceCurrency: 'USD',
            url: 'https://pmaster.uz',
          },
        },
        {
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: 'What is pMaster?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'pMaster is a repair shop CRM designed specifically for the Uzbekistan market to streamline operations.',
              },
            },
            {
              '@type': 'Question',
              name: 'Is pMaster free to use?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'pMaster offers a free plan with essential features and paid plans for advanced capabilities.',
              },
            },
          ],
        },
      ],
    });
    document.head.appendChild(script);

    return () => {
      // Cleanup on unmount is optional
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {!isSupabaseConfigured && (
        <div className="bg-blue-50 border-b border-blue-200 px-4 py-3">
          <div className="max-w-7xl mx-auto flex items-center gap-3">
            <Database className="w-5 h-5 text-blue-600" />
            <p className="text-sm text-blue-800">
              <strong>Database not connected.</strong> Click "Connect to Supabase" in the top right to set up your database and enable all features.
            </p>
          </div>
        </div>
      )}
      <Header />
      <Hero />
      <Problems />
      <Features />
      <HowItWorks />
      <DeviceTypes />
      <Pricing />
      <Testimonials />
      <FAQ />
      <CTA />
      <Footer />
    </div>
  );
}

export default Landing;